import { useEffect, useMemo, useRef, useState } from 'react';
import { NavLink } from 'react-router-dom';
import { Overlay } from 'react-bootstrap';
import { HeaderInstitution } from './header.types';
import classes from './MyInstitutions.module.css';
import { InstitutionView } from 'cb-model';
import { useCacheQuery } from 'gossamer-front-end';

const CURRENT_INSTITUTION_ID_KEY = 'careerbook.currentInstitutionId';

const persistCurrentInstitutionId = (institutionId: number) => {
  if (typeof window !== 'undefined') {
    window.localStorage.setItem(CURRENT_INSTITUTION_ID_KEY, String(institutionId));
  }
};

const getInstitutionHref = (institutionId: number) => `/website/institution/${institutionId}`;

const InstitutionLogoName = ({
  institution,
  truncate = true,
}: {
  institution?: HeaderInstitution;
  truncate?: boolean;
}) => {
  const name = institution?.name ?? '(loading...)';
  const logo = institution?.logo || '/assets/icons/building-library.svg';

  return (
    <div className={classes.institutionInfo}>
      <img className={classes.institutionLogo} src={logo} alt="" loading="lazy" />
      <span className={`${classes.institutionName} ${truncate ? classes.institutionNameTruncated : ''}`.trim()}>
        {name}
      </span>
      {institution?.isAdminInstitution && <span className={classes.adminBadge}>Admin</span>}
    </div>
  );
};

interface MyInstitutionsProps {
  adminInstitutionId: number | null;
  followingInstitutionIds: number[];
  currentInstitutionId?: number | null;
  onInstitutionSelect?: (institutionId: number) => void;
  className?: string;
}

export const MyInstitutions = (props: MyInstitutionsProps) => {
  const [show, setShow] = useState(false);
  const target = useRef<HTMLButtonElement>(null);
  const [selectedInstitutionId, setSelectedInstitutionId] = useState<number | null>(props.currentInstitutionId ?? null);

  const ids = useMemo(() => {
    return (props.followingInstitutionIds || []).filter(
      (id) => typeof id === 'number' && Number.isFinite(id) && id > 0,
    );
  }, [props.followingInstitutionIds]);

  const institutionQuery = useCacheQuery(
    InstitutionView,
    {
      where: { and: [{ fieldId: 'id', operator: 'in', value: ids }] },
      order: [['name', 'ASC']],
      limit: Math.max(ids.length, 1),
    },
    ids.length === 0,
  );

  useEffect(() => {
    if (ids.length === 0) {
      setSelectedInstitutionId(null);
      return;
    }

    if (props.currentInstitutionId && ids.includes(props.currentInstitutionId)) {
      setSelectedInstitutionId(props.currentInstitutionId);
      persistCurrentInstitutionId(props.currentInstitutionId);
      return;
    }

    if (typeof window !== 'undefined') {
      const raw = window.localStorage.getItem(CURRENT_INSTITUTION_ID_KEY);
      const storedId = raw ? Number(raw) : NaN;
      if (Number.isFinite(storedId) && ids.includes(storedId)) {
        setSelectedInstitutionId(storedId);
        return;
      }
    }

    const nextInstitutionId = ids[0];
    setSelectedInstitutionId(nextInstitutionId);
    persistCurrentInstitutionId(nextInstitutionId);
  }, [props.currentInstitutionId, ids]);

  const current = (institutionQuery.data || []).find((institution) => institution.id === selectedInstitutionId);
  const otherInstitutions = (institutionQuery.data || []).filter(
    (institution) => institution.id !== selectedInstitutionId,
  );

  const handleSelect = (institutionId: number) => {
    persistCurrentInstitutionId(institutionId);
    setSelectedInstitutionId(institutionId);
    setShow(false);
    if (props.onInstitutionSelect) {
      props.onInstitutionSelect(institutionId);
    }
  };

  if (ids.length === 0 || !current) return null;

  return (
    <div className={`${classes.root} ${props.className || ''}`.trim()}>
      <div className={classes.splitButton}>
        <NavLink to={getInstitutionHref(current.id)} className={classes.primaryLink}>
          <InstitutionLogoName
            institution={{ ...current, isAdminInstitution: props.adminInstitutionId === current.id }}
          />
        </NavLink>
        <button
          ref={target}
          type="button"
          className={classes.dropdownTrigger}
          aria-label="Select institution"
          aria-expanded={show}
          aria-haspopup="menu"
          disabled={otherInstitutions.length === 0}
          onClick={() => setShow((isShown) => !isShown)}
        />
      </div>
      {otherInstitutions.length > 0 && (
        <Overlay target={target.current} show={show} placement="bottom" rootClose={true} onHide={() => setShow(false)}>
          <div className={classes.panel}>
            <ul className={classes.list}>
              {otherInstitutions.map((institution) => (
                <li key={institution.id}>
                  <NavLink
                    to={getInstitutionHref(institution.id)}
                    className={classes.itemLink}
                    onClick={() => handleSelect(institution.id)}
                  >
                    <InstitutionLogoName
                      institution={{
                        ...institution,
                        isAdminInstitution: props.adminInstitutionId === institution.id,
                      }}
                    />
                  </NavLink>
                </li>
              ))}
            </ul>
          </div>
        </Overlay>
      )}
    </div>
  );
};
